/**
 * Geographic Comparison Selector Component
 * Consolidates 12 separate comparison charts (6 geographies × 2 chart types)
 * into a single selector with 2 charts (funding + M&A)
 *
 * User can select: Israel, Europe, UK, Canada, Japan, or Australia
 * Displays: US vs [Selected Geography] for both Funding and M&A
 */

import React, { useState, useEffect } from 'react';
import { V2DataService } from '../services/V2DataService';
import { ComparisonResponse, ComparisonType, COMPARISON_CONFIGS } from '../types/comparison.types';
import { ComparisonChartSection } from './ComparisonChartSection';
import { ChartErrorBoundary } from './ChartErrorBoundary';
import { ChartMetadata } from './ChartMetadata';
import { log } from '../services/LoggingService';
import { AlertCircle, Globe } from 'lucide-react';

// Geographic options available for comparison
type GeographyOption = 'israel' | 'europe' | 'uk' | 'canada' | 'japan' | 'australia';

interface GeographicComparisonSelectorProps {
  timeframe: string;
  weekOffset?: number;
  filters?: Record<string, string[] | undefined>;
  timeGrouping?: 'weeks' | 'months' | 'quarters';
}

/**
 * Main Geographic Comparison Selector Component
 */
export const GeographicComparisonSelector: React.FC<GeographicComparisonSelectorProps> = ({
  timeframe,
  weekOffset = 0,
  filters,
  timeGrouping = 'weeks'
}) => {
  const [selectedGeography, setSelectedGeography] = useState<GeographyOption>('israel');
  const [comparisonData, setComparisonData] = useState<ComparisonResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Map geographic option to comparison type
  const comparisonType: ComparisonType = `us-${selectedGeography}` as ComparisonType;
  const comparisonConfig = COMPARISON_CONFIGS[comparisonType];

  // Fetch comparison data when geography, timeframe, weekOffset, or filters change
  useEffect(() => {
    const fetchComparisonData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        log.debug(`Fetching comparison data for ${comparisonType}:`, 'GeographicComparison', {
          timeframe,
          weekOffset,
          hasFilters: !!filters
        });

        const v2Service = new V2DataService();
        const data = await v2Service.getComparisonData(
          comparisonType,
          timeframe,
          weekOffset,
          filters
        );

        log.debug(`Comparison data received for ${comparisonType}`, 'GeographicComparison');
        setComparisonData(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch comparison data';
        console.error(`Failed to fetch comparison data for ${comparisonType}:`, err);
        setError(errorMessage);
        log.error(`Failed to fetch comparison data: ${errorMessage}`, 'GeographicComparison');
      } finally {
        setIsLoading(false);
      }
    };

    fetchComparisonData();
  }, [selectedGeography, timeframe, weekOffset, filters, comparisonType]);

  // Geography selection buttons
  const geographyOptions: { key: GeographyOption; label: string; description: string }[] = [
    {
      key: 'israel',
      label: 'Israel',
      description: 'Compare US and Israeli cybersecurity markets'
    },
    {
      key: 'europe',
      label: 'Europe',
      description: 'Compare US and European cybersecurity markets'
    },
    {
      key: 'uk',
      label: 'United Kingdom',
      description: 'Compare US and UK cybersecurity markets'
    },
    {
      key: 'canada',
      label: 'Canada',
      description: 'Compare US and Canadian cybersecurity markets'
    },
    {
      key: 'japan',
      label: 'Japan',
      description: 'Compare US and Japanese cybersecurity markets'
    },
    {
      key: 'australia',
      label: 'Australia',
      description: 'Compare US and Australian cybersecurity markets'
    }
  ];

  return (
    <ChartErrorBoundary chartName="Geographic Comparison">
      <div className="w-full space-y-6">
        {/* Geographic Selector Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Geographic Comparison
            </h2>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Compare United States cybersecurity market with other regions
          </p>

          {/* Geography Selection Buttons */}
          <div className="flex flex-wrap gap-3">
            {geographyOptions.map(({ key, label, description }) => (
              <button
                key={key}
                onClick={() => setSelectedGeography(key)}
                className={`flex-1 min-w-[160px] px-4 py-3 rounded-lg border-2 transition-all ${
                  selectedGeography === key
                    ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20 shadow-md'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-400 dark:hover:border-purple-500'
                }`}
                title={description}
              >
                <div className="text-left">
                  <div className={`font-semibold ${
                    selectedGeography === key
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    US vs {label}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-6">
            {/* Funding Chart Loading Placeholder */}
            <div className="flex items-center justify-center h-[500px] bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                <span className="mt-4 text-gray-600 dark:text-gray-400">
                  Loading {comparisonConfig.label} funding comparison...
                </span>
              </div>
            </div>

            {/* M&A Chart Loading Placeholder */}
            <div className="flex items-center justify-center h-[500px] bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                <span className="mt-4 text-gray-600 dark:text-gray-400">
                  Loading {comparisonConfig.label} M&A comparison...
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex items-center justify-center h-[500px] bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <div className="flex flex-col items-center text-center px-4">
              <AlertCircle className="w-12 h-12 text-red-500 dark:text-red-400 mb-4" />
              <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
                Failed to Load Comparison Charts
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300">
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Charts - Only show when data is loaded and no error */}
        {!isLoading && !error && comparisonData && (
          <>
            {/* Funding Trends Comparison */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <ComparisonChartSection
                data={comparisonData.charts.funding_trends}
                timeframe={timeframe}
                weekOffset={weekOffset}
                isMA={false}
                timeGrouping={timeGrouping}
                filters={filters}
              />
              {comparisonData.metadata && (
                <ChartMetadata
                  periodStart={comparisonData.metadata.current_period.start}
                  periodEnd={comparisonData.metadata.current_period.end}
                  cacheStatus={comparisonData.cache_status}
                  responseTimeMs={comparisonData.response_time_ms}
                />
              )}
            </div>

            {/* M&A Activity Comparison */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <ComparisonChartSection
                data={comparisonData.charts.ma_activity}
                timeframe={timeframe}
                weekOffset={weekOffset}
                isMA={true}
                timeGrouping={timeGrouping}
                filters={filters}
              />
              {comparisonData.metadata && (
                <ChartMetadata
                  periodStart={comparisonData.metadata.current_period.start}
                  periodEnd={comparisonData.metadata.current_period.end}
                  cacheStatus={comparisonData.cache_status}
                  responseTimeMs={comparisonData.response_time_ms}
                />
              )}
            </div>
          </>
        )}

        {/* No Data State */}
        {!isLoading && !error && !comparisonData && (
          <div className="flex items-center justify-center h-[500px] bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-center px-4">
              <p className="text-gray-600 dark:text-gray-400">
                No comparison data available for {comparisonConfig.label}
              </p>
            </div>
          </div>
        )}
      </div>
    </ChartErrorBoundary>
  );
};

export default GeographicComparisonSelector;
